/**
 * UrgencyBadge — sticky top bar with urgency messaging (dotácia countdown,
 * limited capacity). Dismissible for the session. Shows a live countdown
 * for date-based urgency.
 */
import { useEffect, useState } from "react";
import { X, Zap } from "lucide-react";

// Configure these to control the message. Set deadline to `null` to hide countdown.
const DEADLINE = "2026-03-31T23:59:59+01:00";
const HEADLINE = "Dotácia −6 000 € pre zateplenie končí 31.3.2026";
const SUB = "Rezervujte si termín zdarma";
const CTA_HREF = "/#kontakt";
const CTA_LABEL = "Vypýtať ponuku";
const DISMISS_KEY = "sm_urgency_dismissed_v1";

function fmtCountdown(msLeft) {
  if (msLeft <= 0) return null;
  const s = Math.floor(msLeft / 1000);
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `${d}d ${h}h ${m}m`;
}

export default function UrgencyBadge() {
  const [dismissed, setDismissed] = useState(true);
  const [left, setLeft] = useState(null);

  useEffect(() => {
    try {
      const d = localStorage.getItem(DISMISS_KEY);
      setDismissed(d === "1");
    } catch {
      setDismissed(false);
    }
  }, []);

  useEffect(() => {
    if (!DEADLINE) return;
    const tick = () => {
      const ms = new Date(DEADLINE).getTime() - Date.now();
      setLeft(fmtCountdown(ms));
    };
    tick();
    const id = setInterval(tick, 60_000);
    return () => clearInterval(id);
  }, []);

  const close = () => {
    setDismissed(true);
    try {
      localStorage.setItem(DISMISS_KEY, "1");
    } catch {
      /* ignore */
    }
  };

  if (dismissed || (DEADLINE && !left)) return null;

  return (
    <div
      className="relative z-30 bg-gradient-to-r from-[#FF5E00] via-[#ff7a2e] to-[#FF5E00] text-black"
      data-testid="urgency-badge"
    >
      <div className="max-w-7xl mx-auto px-4 py-2 flex items-center gap-3 flex-wrap">
        <Zap size={14} className="flex-shrink-0" />
        <div className="flex-1 min-w-0 text-[13px] font-bold flex items-center gap-2 flex-wrap">
          <span className="truncate">{HEADLINE}</span>
          {left && (
            <span className="bg-black/25 text-white px-2 py-0.5 rounded-full text-[11px] font-mono tabular-nums">
              {left}
            </span>
          )}
          <span className="hidden sm:inline text-black/70 font-normal">— {SUB}</span>
        </div>
        <a
          href={CTA_HREF}
          className="hidden sm:inline-flex items-center gap-1 px-3 py-1 bg-black text-white rounded-full text-[12px] font-bold hover:bg-zinc-900 transition-colors"
          data-testid="urgency-cta"
        >
          {CTA_LABEL} →
        </a>
        <button
          onClick={close}
          aria-label="Zavrieť"
          className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-black/15 transition-colors"
          data-testid="urgency-close"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}
